import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ChatFeedback,
  ChatMessage,
  ChatMessageChoice,
  SendMessageResult,
  StrategistConversation,
  StrategistConversationWithMessages,
} from '@mg/shared';
import { Check, Copy, Plus, Send, Sparkles, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { DesignActionCard } from './DesignActionCard';
import { composerRows } from './composerRows';
import { userMessageText } from './choiceLabel';
import { chatScrollAction } from './chatScroll';
import { readChatSeed, type ChatSeed } from './chatSeed';
import { liveChoiceIndex, questionFor } from './liveChoice';
import { useTenant } from '../../auth/TenantContext';
import { useTenantKey } from '../../lib/useTenantKey';
import { formatRelative } from '../../lib/display';
import { cn } from '../../lib/cn';
import { Spinner } from '../../components/ui/Spinner';
import { ErrorState } from '../../components/ui/States';
import { Markdown } from '../../components/ui/Markdown';

const SUGGESTIONS = [
  'Give me 5 blog post ideas for our audience',
  "What's a good content strategy for our next campaign?",
  'Draft a social media campaign outline',
  'How should we position against competitors?',
  'What content angles work best on LinkedIn?',
];

function welcomeText(tenantName: string): string {
  return (
    `Hi! I'm your AI Content Strategist for ${tenantName}. I can help you brainstorm ` +
    `content ideas, develop marketing strategies, craft messaging, and plan campaigns. ` +
    `What would you like to work on today?`
  );
}

/** AI Strategist — a full LLM-style chat with a conversation sidebar. */
export function StrategistChat() {
  const { activeTenant } = useTenant();
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const tenantName = activeTenant?.name ?? 'your team';

  // Open a specific conversation when deep-linked via ?c=<id>. Falls back to auto-selecting the
  // most recent.
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState<string | null>(() => searchParams.get('c'));

  /**
   * What another page handed over to become this page's first message: a theme, from "Write Post
   * with Strategist" on Trends (or the Dashboard's trends widget), or the text typed into the
   * Dashboard's "What do you want to write about?" box.
   *
   * Those controls navigate here IMMEDIATELY and leave the seeding to this page, because the seeding
   * ends in a model call: done before navigating, the wait happened on a page that could show
   * nothing about it. Read once at mount — a later render must not re-seed.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const [seed] = useState<ChatSeed | null>(() => readChatSeed(location.state));
  /** True from mount until the seed's first message has been fired — the gap the page must cover. */
  const [seedPending, setSeedPending] = useState(Boolean(seed));
  const [seedError, setSeedError] = useState<string | null>(null);
  const seedStarted = useRef(false);
  /**
   * A conversation whose thread must NOT be re-fetched yet.
   *
   * A seeded conversation is created empty and its first message is sent straight afterwards. The
   * server does not know about that message until the send returns, so a fetch in the meantime
   * answers "this thread has no messages" — truthfully, and it wipes the prompt off the screen
   * exactly while the user is meant to be reading it. We already know the thread is empty; there is
   * nothing to learn from asking until the send is done.
   */
  const [unfetchedSeedId, setUnfetchedSeedId] = useState<string | null>(null);
  // The assistant message to consider "newly arrived" for top-anchoring — set ONLY on a successful
  // send, so a failed send / conversation switch never triggers the top-anchor scroll.
  const [anchorId, setAnchorId] = useState<string | null>(null);

  const conversationsQuery = useQuery({
    queryKey: ['strategist-conversations', tenantKey],
    queryFn: ({ signal }) => api.listConversations(signal),
    enabled: tenantKey !== '',
  });

  const conversations = useMemo(() => conversationsQuery.data ?? [], [conversationsQuery.data]);

  // Auto-select the most recent conversation once the list resolves — but never while a seed is
  // landing, or the page would flash somebody's previous chat on the way to the new one.
  useEffect(() => {
    if (activeId || seedPending || conversations.length === 0) return;
    setActiveId(conversations[0].id);
  }, [conversations, activeId, seedPending]);

  const conversationQuery = useQuery({
    queryKey: ['strategist-conversation', tenantKey, activeId],
    queryFn: ({ signal }) => api.getConversation(activeId as string, signal),
    // Cached data still renders while this is false — only the network call is held back.
    enabled: Boolean(activeId) && tenantKey !== '' && activeId !== unfetchedSeedId,
  });

  // Recover from a stale/foreign deep-link (?c=<deleted or other-owner id>): once the
  // list has resolved, if the active conversation failed to LOAD and isn't in the list,
  // fall back to auto-selecting. Gated on the load error (not mere absence) so a freshly
  // seeded conversation still refetching into the list isn't dropped.
  useEffect(() => {
    if (!conversationQuery.isError || conversationsQuery.isLoading) return;
    if (!activeId || conversations.some((c) => c.id === activeId)) return;
    setActiveId(conversations[0]?.id ?? null);
  }, [conversationQuery.isError, conversationsQuery.isLoading, conversations, activeId]);

  const createConversation = useMutation({
    mutationFn: () => api.createConversation(),
    onSuccess: (created) => {
      queryClient.setQueryData<StrategistConversation[]>(
        ['strategist-conversations', tenantKey],
        (prev) => [created, ...(prev ?? [])],
      );
      setActiveId(created.id);
    },
  });

  const deleteConversation = useMutation({
    mutationFn: (id: string) => api.deleteConversation(id),
    onSuccess: (_void, id) => {
      queryClient.setQueryData<StrategistConversation[]>(
        ['strategist-conversations', tenantKey],
        (prev) => (prev ?? []).filter((c) => c.id !== id),
      );
      if (activeId === id) setActiveId(null);
    },
  });

  const sendMessage = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      api.sendStrategistMessage(id, content),
    onMutate: async ({ id, content }) => {
      const key = ['strategist-conversation', tenantKey, id] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<StrategistConversationWithMessages>(key);
      // Optimistically append the user's message.
      if (previous) {
        const optimistic: ChatMessage = {
          id: `optimistic-${Date.now()}`,
          conversationId: id,
          role: 'user',
          content,
          createdAt: new Date().toISOString(),
        };
        queryClient.setQueryData<StrategistConversationWithMessages>(key, {
          ...previous,
          messages: [...previous.messages, optimistic],
        });
      }
      return { previous, key };
    },
    onSuccess: (result: SendMessageResult, { id }) => {
      const key = ['strategist-conversation', tenantKey, id] as const;
      queryClient.setQueryData<StrategistConversationWithMessages>(key, (prev) => {
        if (!prev) return prev;
        // Drop any optimistic placeholders, then append the persisted pair.
        const persisted = prev.messages.filter((m) => !m.id.startsWith('optimistic-'));
        return {
          ...prev,
          ...result.conversation,
          messages: [...persisted, result.userMessage, result.assistantMessage],
        };
      });
      // Keep the sidebar (title/count/order) fresh.
      void queryClient.invalidateQueries({
        queryKey: ['strategist-conversations', tenantKey],
      });
      // Mark this specific assistant reply for top-anchoring (long replies start at their top).
      setAnchorId(result.assistantMessage.id);
    },
    onError: (_err, { id }, context) => {
      // Take back the optimistic message and nothing else. Putting the pre-send snapshot back
      // would also undo whatever changed the thread while the send was in flight — a thumbs
      // rating the server has already saved would be wiped off the screen.
      if (context?.previous) {
        queryClient.setQueryData<StrategistConversationWithMessages>(context.key, (thread) =>
          thread
            ? {
                ...thread,
                messages: thread.messages.filter((m) => !m.id.startsWith('optimistic-')),
              }
            : thread,
        );
      }
      void id;
    },
    // The send has landed (either way), so the server's copy is now the better one.
    onSettled: () => setUnfetchedSeedId(null),
  });

  /**
   * Turn what was handed over into this page's first exchange.
   *
   * Either way there is a new, EMPTY conversation and a prompt that has not been sent: for a theme
   * the server returns both, for typed text the conversation is created here and the prompt is the
   * text. The prompt is then sent from here like any other message — which is the whole point: the
   * optimistic append puts it on screen immediately, the typing indicator runs while the model
   * works, and the reply lands through the same path that scrolls to the TOP of a long answer.
   * Doing it before the page existed got all three wrong at once.
   */
  useEffect(() => {
    if (!seed || seedStarted.current) return;
    seedStarted.current = true;
    // History state survives a reload, so drop it before anything else: refreshing this page must
    // not open a second conversation on the same theme or prompt.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });

    void (async () => {
      try {
        // The only step that differs between the two seeds. Everything after it is shared, and
        // has to be: the cache priming and the fetch guard below are what keep the prompt on screen.
        const { conversation, seedMessage } =
          seed.kind === 'theme'
            ? await api.themeToStrategistChat(seed.theme.briefId, seed.theme.themeId)
            : { conversation: await api.createConversation(), seedMessage: seed.text };
        queryClient.setQueryData<StrategistConversation[]>(
          ['strategist-conversations', tenantKey],
          (prev) => [conversation, ...(prev ?? [])],
        );
        /**
         * Prime the thread cache BEFORE sending.
         *
         * sendMessage's optimistic append is skipped when the conversation has not been fetched
         * yet — and that append IS the prompt-while-you-wait. Without this the page would sit on a
         * typing indicator with nothing above it until the whole answer arrived.
         */
        queryClient.setQueryData<StrategistConversationWithMessages>(
          ['strategist-conversation', tenantKey, conversation.id],
          { ...conversation, messages: [] },
        );
        setUnfetchedSeedId(conversation.id);
        setActiveId(conversation.id);
        sendMessage.mutate({ id: conversation.id, content: seedMessage });
      } catch (err) {
        setSeedError(
          err instanceof ApiRequestError
            ? err.message
            : seed.kind === 'theme'
              ? 'Could not start a chat about that trend.'
              : 'Could not start a conversation.',
        );
      } finally {
        // Cleared in the same turn the send is fired, so the waiting state never blinks off between
        // the two.
        setSeedPending(false);
      }
    })();
    // Runs once for the seed captured at mount; `navigate`/`location` are stable enough here and
    // re-running on them would re-seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  /** Send into an existing or freshly-created conversation. */
  const handleSend = async (text: string) => {
    const content = text.trim();
    if (!content || sendMessage.isPending) return;
    let id = activeId;
    if (!id) {
      const created = await createConversation.mutateAsync();
      id = created.id;
    }
    sendMessage.mutate({ id, content });
  };

  const activeConversation = conversationQuery.data ?? null;
  const messages = activeConversation?.messages ?? [];
  const sendError = sendMessage.error instanceof ApiRequestError ? sendMessage.error.message : null;
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <div className="flex h-full min-h-0 overflow-hidden rounded-xl border border-line bg-card shadow-card">
      <ConversationSidebar
        conversations={conversations}
        activeId={activeId}
        loading={conversationsQuery.isLoading}
        creating={createConversation.isPending}
        onSelect={setActiveId}
        onNew={() => createConversation.mutate()}
        onDelete={(id) => deleteConversation.mutate(id)}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-line-soft px-5 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-action">
            <Sparkles className="h-5 w-5 text-action-fg" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">Content Strategist</h2>
            <p className="truncate text-sm text-ink-muted">
              {tenantName} · AI-powered marketing assistant
            </p>
          </div>
          {/* The conversation being read, opposite the product name. Hidden rather than blank when
              there is nothing open yet, so the bar doesn't show a stray label over an empty pane. */}
          {activeConversation && (
            <p
              className="ml-auto min-w-0 shrink truncate pl-3 text-sm font-medium text-ink-strong"
              title={activeConversation.title}
            >
              {activeConversation.title}
            </p>
          )}
        </header>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-6">
          {conversationQuery.isError ? (
            <ErrorState
              message={
                conversationQuery.error instanceof ApiRequestError
                  ? conversationQuery.error.message
                  : 'Could not load this conversation.'
              }
              onRetry={() => void conversationQuery.refetch()}
            />
          ) : (
            <MessageList
              // Remount per conversation so scroll state resets — otherwise switching between two
              // cached conversations with the same message count wouldn't re-run the scroll effect.
              key={activeId ?? 'none'}
              messages={messages}
              tenantName={tenantName}
              awaitingReply={sendMessage.isPending || seedPending}
              anchorId={anchorId}
              onSuggestion={(text) => void handleSend(text)}
              onChoose={(value) => void handleSend(value)}
              scrollRef={scrollRef}
            />
          )}
        </div>

        {(seedError ?? sendError) && (
          <p className="px-5 pb-2 text-sm text-red-600 dark:text-error-dark" role="alert">
            {seedError ?? sendError}
          </p>
        )}

        <Composer
          disabled={conversationQuery.isError}
          sending={sendMessage.isPending}
          onSend={(text) => void handleSend(text)}
        />
      </div>
    </div>
  );
}

function ConversationSidebar({
  conversations,
  activeId,
  loading,
  creating,
  onSelect,
  onNew,
  onDelete,
}: {
  conversations: StrategistConversation[];
  activeId: string | null;
  loading: boolean;
  creating: boolean;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}) {
  return (
    <aside className="flex w-[16.25rem] shrink-0 flex-col border-r border-line-soft bg-page/50">
      <div className="p-3">
        <button
          type="button"
          onClick={onNew}
          disabled={creating}
          className="focus-ring flex w-full items-center justify-center gap-2 rounded-lg bg-action px-3 py-2 text-sm font-medium text-action-fg transition-colors hover:bg-action-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {creating ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          New Conversation
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {loading ? (
          <p className="px-2 py-3 text-sm text-ink-faint">Loading…</p>
        ) : conversations.length === 0 ? (
          <p className="px-2 py-3 text-sm text-ink-faint">
            No conversations yet. Start a new one to begin.
          </p>
        ) : (
          <ul className="space-y-1">
            {conversations.map((c) => {
              const active = c.id === activeId;
              return (
                <li key={c.id}>
                  <div
                    className={cn(
                      'group relative rounded-lg border transition-colors',
                      active ? 'border-ink-ghost bg-tint' : 'border-transparent hover:bg-card',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => onSelect(c.id)}
                      aria-current={active ? 'true' : undefined}
                      className="focus-ring block w-full rounded-lg px-3 py-2 pr-8 text-left"
                    >
                      <span
                        className={cn(
                          'block truncate text-sm font-medium',
                          active ? 'text-tint-fg' : 'text-ink',
                        )}
                      >
                        {c.title || 'New conversation'}
                      </span>
                      <span className="mt-0.5 block text-xs text-ink-faint">
                        {c.messageCount} message{c.messageCount === 1 ? '' : 's'} ·{' '}
                        {formatRelative(c.updatedAt)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(c.id)}
                      aria-label={`Delete ${c.title || 'conversation'}`}
                      className="focus-ring absolute right-1.5 top-1.5 rounded p-1 text-ink-faint opacity-0 transition-opacity hover:bg-chip hover:text-red-600 dark:hover:text-error-dark focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}

function MessageList({
  messages,
  tenantName,
  awaitingReply,
  anchorId,
  onSuggestion,
  onChoose,
  scrollRef,
}: {
  messages: ChatMessage[];
  tenantName: string;
  awaitingReply: boolean;
  /** Id of a just-arrived assistant reply to top-anchor (set only on a successful send). */
  anchorId: string | null;
  onSuggestion: (text: string) => void;
  /** Send a picked option value as the next user message (drives the plan forward). */
  onChoose: (value: string) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
}) {
  const live = liveChoiceIndex(messages);
  const endRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const prevAnchorId = useRef(anchorId);
  /**
   * Whether this conversation has been put in its opening position yet.
   *
   * Starts false for every conversation opened, because switching remounts the list (see `key`).
   * It cannot simply be "the first effect run": the list mounts while the fetch is still in
   * flight, so the first run sees an empty transcript and has nothing to scroll to.
   */
  const hasPositioned = useRef(false);

  // useLayoutEffect, not useEffect: this runs before the browser paints, so opening a conversation
  // shows the bottom of the thread outright. With useEffect the top is painted first and the jump
  // lands a frame later, which is the flash-then-scroll this replaced.
  useLayoutEffect(() => {
    // Anchor to the TOP of the reply ONLY when anchorId just CHANGED (a genuine new successful
    // reply — not a failed send, and not a re-render) AND that reply is taller than the viewport,
    // so a long answer starts at its beginning instead of the user having to scroll up. Everything
    // else — sending, short replies — falls through to the bottom, the usual chat behaviour.
    const isNewReply = anchorId !== null && anchorId !== prevAnchorId.current;
    prevAnchorId.current = anchorId;

    const anchor = anchorRef.current;
    const container = scrollRef.current;

    switch (
      chatScrollAction({
        hasPositioned: hasPositioned.current,
        messageCount: messages.length,
        isNewReply,
        replyTallerThanViewport: Boolean(
          anchor && container && anchor.offsetHeight > container.clientHeight - 48,
        ),
      })
    ) {
      case 'none':
        return;
      // Setting scrollTop directly rather than scrollIntoView: there is nothing to animate FROM,
      // and this lands before the browser paints, so the bottom of the thread is the first thing
      // drawn instead of the top followed by a ride down it.
      case 'jump-to-end':
        hasPositioned.current = true;
        if (container) container.scrollTop = container.scrollHeight;
        return;
      case 'anchor-top':
        anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      case 'smooth-to-end':
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages.length, awaitingReply, anchorId, scrollRef]);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      {messages.length === 0 && !awaitingReply ? (
        <WelcomeMessage tenantName={tenantName} onSuggestion={onSuggestion} />
      ) : (
        messages.map((m, i) => (
          <MessageRow
            key={m.id}
            message={m}
            // The question this message may be answering — it carries the button labels.
            previous={questionFor(messages, i)}
            anchorRef={m.id === anchorId ? anchorRef : undefined}
            // Interactive option buttons are live only on the latest reply that is not a failed
            // turn (see liveChoice) and only when not awaiting a reply — historical choices just
            // show their question text.
            interactive={i === live && !awaitingReply}
            onChoose={onChoose}
          />
        ))
      )}

      {awaitingReply && <TypingIndicator />}
      <div ref={endRef} />
    </div>
  );
}

function WelcomeMessage({
  tenantName,
  onSuggestion,
}: {
  tenantName: string;
  onSuggestion: (text: string) => void;
}) {
  return (
    <div className="flex gap-3">
      <AssistantAvatar />
      <div className="min-w-0 flex-1 space-y-4">
        <div className="rounded-2xl rounded-tl-sm border border-line bg-page px-4 py-3 text-sm leading-relaxed text-ink">
          {welcomeText(tenantName)}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-faint">
            Try asking about:
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onSuggestion(s)}
                className="focus-ring rounded-full border border-line-input bg-card px-3 py-1.5 text-sm text-link transition-colors hover:border-ink-ghost hover:bg-tint"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageRow({
  message,
  previous,
  anchorRef,
  interactive = false,
  onChoose,
}: {
  message: ChatMessage;
  /** The question this message may be answering (questionFor) — userMessageText reads its labels. */
  previous?: ChatMessage | null;
  /** Set on the LAST assistant row so the list can scroll to its top for long replies. */
  anchorRef?: React.Ref<HTMLDivElement>;
  /** Whether this row's option buttons (if any) are live (liveChoiceIndex, not awaiting a reply). */
  interactive?: boolean;
  onChoose?: (value: string) => void;
}) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        {/* whitespace-pre-wrap preserves the line breaks the user typed (and the ones in a
            "Send to Strategist" seeded message) — as bare text in HTML they collapse into one
            paragraph. Deliberately NOT rendered as markdown: user-supplied text stays
            React-escaped. break-words keeps a long unbroken URL from overflowing the bubble. */}
        <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl rounded-tr-sm bg-action px-4 py-3 text-sm leading-relaxed text-action-fg">
          {userMessageText(message, previous)}
        </div>
      </div>
    );
  }

  const choice = message.data?.kind === 'choice' ? message.data : null;
  // Unlike a choice, the design card stays actionable in history — the bundle it offers is built
  // from the saved post, which is still there long after the turn ended.
  const designAction = message.data?.kind === 'design-action' ? message.data : null;

  return (
    <div ref={anchorRef} className="flex scroll-mt-4 gap-3">
      <AssistantAvatar />
      <div className="min-w-0 flex-1">
        <div className="rounded-2xl rounded-tl-sm border border-line bg-page px-4 py-3 text-ink">
          <Markdown content={message.content} />
        </div>
        {choice && interactive && onChoose && <ChoiceButtons choice={choice} onChoose={onChoose} />}
        {designAction && <DesignActionCard action={designAction} />}
        <MessageActions message={message} />
      </div>
    </div>
  );
}

/**
 * Clickable option buttons for a Strategist `present_choice` — the "Claude Code" step-by-step UX.
 * Clicking an option (or submitting the "Other" free-text) sends that value as the user's next
 * message, advancing the plan. Rendered only on the latest, non-pending assistant message — or,
 * when the turn after it failed, on the question that turn was meant to answer (see liveChoice).
 */
function ChoiceButtons({
  choice,
  onChoose,
}: {
  choice: ChatMessageChoice;
  onChoose: (value: string) => void;
}) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [other, setOther] = useState('');

  const submitOther = () => {
    const text = other.trim();
    if (text) onChoose(text);
  };

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {choice.options.map((opt, i) => (
        <button
          key={`${opt.value}-${i}`}
          type="button"
          onClick={() => onChoose(opt.value)}
          title={opt.description}
          className="focus-ring rounded-full border border-line-input bg-card px-3 py-1.5 text-sm font-medium text-link transition-colors hover:border-ink-ghost hover:bg-tint"
        >
          {opt.label}
        </button>
      ))}

      {choice.allowOther &&
        (otherOpen ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitOther();
            }}
            className="flex items-center gap-1.5"
          >
            <input
              autoFocus
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder={choice.otherLabel || 'Something else…'}
              className="focus-ring w-52 rounded-full border border-line-input bg-card px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint"
            />
            <button
              type="submit"
              disabled={other.trim() === ''}
              className="focus-ring rounded-full bg-action px-3 py-1.5 text-sm font-medium text-action-fg hover:bg-action-hover disabled:cursor-not-allowed disabled:opacity-40"
            >
              Send
            </button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setOtherOpen(true)}
            className="focus-ring rounded-full border border-dashed border-line-dashed px-3 py-1.5 text-sm text-ink-muted transition-colors hover:bg-hover"
          >
            {choice.otherLabel || 'Other…'}
          </button>
        ))}
    </div>
  );
}

function MessageActions({ message }: { message: ChatMessage }) {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  /** The last rating could not be saved. Cleared by the next click on a thumb. */
  const [saveFailed, setSaveFailed] = useState(false);
  const feedback = message.feedback ?? null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context) — ignore.
    }
  };

  const threadKey = ['strategist-conversation', tenantKey, message.conversationId] as const;
  /** Change THIS message in the cached thread, leaving every other message as it is. */
  const patchMessage = (change: (current: ChatMessage) => ChatMessage) =>
    queryClient.setQueryData<StrategistConversationWithMessages>(threadKey, (thread) =>
      thread
        ? { ...thread, messages: thread.messages.map((m) => (m.id === message.id ? change(m) : m)) }
        : thread,
    );

  const rate = useMutation({
    mutationFn: (rating: ChatFeedback | null) =>
      api.setStrategistMessageFeedback(message.conversationId, message.id, rating),
    // The thumb fills in at once; if the server does not take it, onError puts the old one back.
    onMutate: async (rating) => {
      await queryClient.cancelQueries({ queryKey: threadKey });
      patchMessage((m) => ({ ...m, feedback: rating }));
      return { previous: feedback };
    },
    onError: (_err, _rating, context) => {
      patchMessage((m) => ({ ...m, feedback: context?.previous ?? null }));
      setSaveFailed(true);
    },
    onSuccess: (saved) => patchMessage(() => saved),
  });

  /** Clicking the thumb that is already selected takes the rating back; the other one switches. */
  const choose = (thumb: ChatFeedback) => {
    setSaveFailed(false);
    rate.mutate(feedback === thumb ? null : thumb);
  };

  const iconBtnBase = 'focus-ring rounded p-1 transition-colors disabled:cursor-not-allowed';
  const iconBtn = `${iconBtnBase} text-ink-faint hover:bg-chip hover:text-ink-label`;

  return (
    <>
      <div className="mt-1.5 flex items-center gap-1">
        <button
          type="button"
          onClick={() => void copy()}
          className={iconBtn}
          aria-label="Copy message"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-success dark:text-success-dark" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          onClick={() => choose('up')}
          disabled={rate.isPending}
          aria-pressed={feedback === 'up'}
          className={feedback === 'up' ? `${iconBtnBase} bg-success-tint dark:bg-success-dark-tint text-success dark:text-success-dark` : iconBtn}
          aria-label="Good response"
          title="Good response"
        >
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => choose('down')}
          disabled={rate.isPending}
          aria-pressed={feedback === 'down'}
          className={feedback === 'down' ? `${iconBtnBase} bg-red-50 dark:bg-error-dark-tint text-red-600 dark:text-error-dark` : iconBtn}
          aria-label="Bad response"
          title="Bad response"
        >
          <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      {saveFailed && (
        <p className="mt-1 text-xs text-red-600 dark:text-error-dark" role="alert">
          Could not save your feedback.
        </p>
      )}
    </>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-3">
      <AssistantAvatar />
      <div className="flex items-center gap-1.5 rounded-2xl rounded-tl-sm border border-line bg-page px-4 py-3.5">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-2 w-2 animate-bounce rounded-full bg-ink-ghost"
            style={{ animationDelay: `${delay}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function AssistantAvatar() {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-action">
      <Sparkles className="h-4 w-4 text-action-fg" aria-hidden="true" />
    </div>
  );
}

function Composer({
  disabled,
  sending,
  onSend,
}: {
  disabled: boolean;
  sending: boolean;
  onSend: (text: string) => void;
}) {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  /** @see composerRows — set by a send, cleared by the next thing the user does. */
  const [collapsedBySend, setCollapsedBySend] = useState(false);

  const rows = composerRows({ value, focused, collapsedBySend });

  const submit = () => {
    const text = value.trim();
    if (!text || sending) return;
    onSend(text);
    setValue('');
    setCollapsedBySend(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="shrink-0 border-t border-line-soft px-5 py-3">
      {/* items-START: once the box grows the send button belongs at the top edge, beside the first
          line being typed, not floating level with the bottom of a ten-row text area. */}
      <div className="flex items-start gap-2">
        <textarea
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setCollapsedBySend(false);
          }}
          onFocus={() => {
            setFocused(true);
            setCollapsedBySend(false);
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          // Two rows at rest; a paragraph's worth while writing — briefs pasted in here are
          // routinely far longer than the one line this used to show.
          rows={rows}
          aria-label="Message the Content Strategist"
          placeholder="Ask me anything about content strategy, marketing ideas, or campaign planning..."
          // No max-height: it was capped at 10rem, which would clip the ten rows just asked for.
          className="focus-ring flex-1 resize-y rounded-lg border border-line-input bg-card px-3 py-2 text-sm text-ink placeholder:text-ink-faint disabled:bg-page"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || sending || value.trim() === ''}
          aria-label="Send message"
          className="focus-ring flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-action text-action-fg transition-colors hover:bg-action-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          {sending ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <Send className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
      <p className="mt-1.5 text-xs text-ink-faint">Press Enter to send, Shift+Enter for new line</p>
    </div>
  );
}
